import { createLogger } from "@skaile/workspaces/core/logging";
import type { Logger } from "@skaile/workspaces/types";
import type {
  ConnectContext,
  Connector,
  ConnectorChangeEvent,
  ConnectorDeclaration,
  ConnectorHandle,
} from "./connector-types.js";
import type { WatchHandle, WatchOptions } from "./shared-types.js";

/**
 * Base class for unified connectors. Subclasses set `filesystem` and/or `tools`
 * face objects and implement `connect`/`disconnect`. The manager replaces
 * `this.log` via `setLogger()` after construction — do not initialise your own.
 */
export abstract class AbstractConnector implements Connector {
  abstract readonly name: string;
  protected log: Logger = createLogger({ kind: "connector", subkind: "unknown" });
  setLogger(logger: Logger): void {
    this.log = logger;
  }

  /** Set by the default `watch()`; call after mutations to emit change events. */
  protected emitChange?: (event: ConnectorChangeEvent) => void;

  abstract connect(
    declaration: ConnectorDeclaration,
    ctx: ConnectContext,
  ): Promise<ConnectorHandle>;
  abstract disconnect(handle: ConnectorHandle): Promise<void>;

  watch(
    _handle: ConnectorHandle,
    callback: (e: ConnectorChangeEvent) => void,
    _options?: WatchOptions,
  ): WatchHandle {
    this.emitChange = callback;
    return {
      close: async () => {
        this.emitChange = undefined;
      },
    };
  }

  async onHibernate(_handle: ConnectorHandle): Promise<string | undefined> {
    return undefined;
  }
  async onSessionClose(_handle: ConnectorHandle): Promise<string | undefined> {
    return undefined;
  }

  /** Build a handle. Pass `mountPath` only for mountable connectors. */
  protected makeHandle(
    declaration: ConnectorDeclaration,
    state: unknown,
    mountPath?: string,
  ): ConnectorHandle {
    return {
      id: declaration.id,
      driver: this.name,
      access: declaration.access,
      status: "connected",
      mountPath,
      state,
    };
  }
}
